<template>
  <template v-if="sidebarComponents.length === 0">
    <section
      v-if="variants.v1 === variant"
      class="layout-pt-lg layout-pb-lg"
      :style="`background-color: var(--c-${vars.backgroundColor})`"
    >
      <div class="container">
        <div class="row justify-between items-center">
          <div class="col-auto">
            <h2 class="text-62 md:text-38 uppercase fw-500" v-html="vars.titleHtml" />
          </div>

          <div class="col-xl-5 tw-hidden tw-gap-2 xl:tw-flex">
            <nuxt-link
              v-for="button in buttons"
              :key="`latestproperties-button-${button.label}`"
              :to="button.link ? String(button.link) : '#'"
              :external="true"
              :class="`n-${button.type}`"
            >
              {{ button.label }}
            </nuxt-link>
          </div>
        </div>

        <div
          class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider lg:!tw-mt-10 lg:tw-flex lg:tw-flex-col-reverse"
          data-anim="slide-left delay-1"
          data-gap="30"
          data-slider-cols="xl-3 lg-2 md-1 sm-1 base-1"
        >
          <div class="swiper-wrapper">
            <div
              class="swiper-slide"
              v-for="(property, index) in mixedProperties"
              :key="`latestproperties-property-${index}`"
            >
              <theme7-properties-property-list-item
                :property="property"
                :version="componentVersion"
                :is-match-information-hidden="true"
                :pre-market-property-feature="true"
                :pre-market-blur-text="vars.preMarketBlurredImageTextHtml"
              />
            </div>
          </div>

          <div class="d-flex mt-60 md:mt-30 max-lg:tw-items-center max-lg:tw-justify-center lg:!tw-mt-0 lg:tw-mb-8">
            <button class="sliderNav -prev js-prev n-link" @click="slidePrev">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next ml-20 js-next n-link" @click="slideNext">
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>

        <div class="tw-flex tw-w-full tw-flex-col tw-gap-2 tw-pt-10 lg:tw-flex-row xl:tw-hidden">
          <nuxt-link
            v-for="button in buttons"
            :key="`latestproperties-button-${button.label}`"
            :to="button.link ? String(button.link) : '#'"
            :external="true"
            :class="`n-${button.type}`"
          >
            {{ button.label }}
          </nuxt-link>
        </div>
      </div>
    </section>

    <section
      v-else-if="variants.v2 === variant"
      class="layout-pt-lg layout-pb-lg"
      :style="`background-color: var(--c-${vars.backgroundColor})`"
    >
      <div class="container">
        <div class="row justify-between items-center">
          <div class="col-auto">
            <h2 v-if="vars.titleHtml" class="uppercase" v-html="vars.titleHtml" />
            <h2 v-else-if="!vars.titleHtml && vars.titleText" class="uppercase">
              {{ vars.titleText }}
            </h2>
          </div>

          <div class="col-xl-5 tw-hidden tw-justify-end tw-gap-2 xl:tw-flex">
            <nuxt-link
              v-for="button in buttons"
              :key="`latestproperties-button-${button.label}`"
              :to="button.link ? String(button.link) : '#'"
              :external="true"
              :class="`n-${button.type}`"
            >
              {{ button.label }}
              <span
                v-if="company?.company_key === 'keystonepmc' && button.type?.toString().includes('secondary icon')"
                class="btn-arrow"
              >
                <svg width="23" height="9" viewBox="0 0 23 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22.3536 4.85355C22.5488 4.65829 22.5488 4.34171 22.3536 4.14645L19.1716 0.964466C18.9763 0.769204 18.6597 0.769204 18.4645 0.964466C18.2692 1.15973 18.2692 1.47631 18.4645 1.67157L21.2929 4.5L18.4645 7.32843C18.2692 7.52369 18.2692 7.84027 18.4645 8.03553C18.6597 8.2308 18.9763 8.2308 19.1716 8.03553L22.3536 4.85355ZM0 4.5V5H22V4.5V4H0V4.5Z"
                    fill="var(--c-text_primary)"
                  />
                </svg>
              </span>
            </nuxt-link>
          </div>
        </div>

        <div
          class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider"
          data-anim="slide-left delay-1"
          data-gap="30"
          data-slider-cols="xl-3 lg-2 md-1 sm-1 base-1"
        >
          <div class="swiper-wrapper">
            <div
              class="swiper-slide"
              v-for="(property, index) in properties"
              :key="`latestproperties-property-${index}`"
            >
              <theme7-properties-property-list-item
                :property="property"
                :is-match-information-hidden="true"
                :pre-market-property-feature="true"
                :pre-market-blur-text="vars.preMarketBlurredImageTextHtml"
                :version="componentVersion"
              />
            </div>
          </div>

          <div class="d-flex mt-60 md:mt-30 max-lg:tw-items-center max-lg:tw-justify-center">
            <button class="sliderNav -prev js-prev n-link" @click="slidePrev">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next ml-20 js-next n-link" @click="slideNext">
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>

        <div class="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-2 tw-pt-10 lg:tw-flex-row xl:tw-hidden">
          <nuxt-link
            v-for="button in buttons"
            :key="`latestproperties-button-${button.label}`"
            :to="button.link ? String(button.link) : '#'"
            :external="true"
            :class="`tw-inline-flex tw-w-auto n-${button.type}`"
          >
            {{ button.label }}
            <span
              v-if="company?.company_key === 'keystonepmc' && button.type?.toString().includes('secondary icon')"
              class="btn-arrow"
            >
              <svg width="23" height="9" viewBox="0 0 23 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22.3536 4.85355C22.5488 4.65829 22.5488 4.34171 22.3536 4.14645L19.1716 0.964466C18.9763 0.769204 18.6597 0.769204 18.4645 0.964466C18.2692 1.15973 18.2692 1.47631 18.4645 1.67157L21.2929 4.5L18.4645 7.32843C18.2692 7.52369 18.2692 7.84027 18.4645 8.03553C18.6597 8.2308 18.9763 8.2308 19.1716 8.03553L22.3536 4.85355ZM0 4.5V5H22V4.5V4H0V4.5Z"
                  fill="var(--c-text_primary)"
                />
              </svg>
            </span>
          </nuxt-link>
        </div>
      </div>
    </section>

    <section
      v-else-if="variants.v3 === variant"
      class="layout-pt-lg layout-pb-lg"
      :style="`background-color: var(--c-${vars.backgroundColor})`"
    >
      <div class="container">
        <div class="row justify-between items-center">
          <div class="col-auto">
            <h2 v-if="vars.titleHtml" class="text-62 md:text-38 uppercase fw-500" v-html="vars.titleHtml" />
            <h2 v-else-if="!vars.titleHtml && vars.titleText" :style="`color: var(--c-${vars.textColor})`">
              {{ vars.titleText }}
            </h2>
          </div>

          <div class="col-xl-5 tw-hidden tw-justify-end tw-gap-2 xl:tw-flex">
            <nuxt-link
              v-for="button in buttons"
              :key="`latestproperties-button-${button.label}`"
              :to="button.link ? String(button.link) : '#'"
              :external="true"
              :class="`n-${button.type}`"
            >
              {{ button.label }}
              <span
                v-if="company?.company_key === 'keystonepmc' && button.type?.toString().includes('secondary icon')"
                class="btn-arrow"
              >
                <svg width="23" height="9" viewBox="0 0 23 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22.3536 4.85355C22.5488 4.65829 22.5488 4.34171 22.3536 4.14645L19.1716 0.964466C18.9763 0.769204 18.6597 0.769204 18.4645 0.964466C18.2692 1.15973 18.2692 1.47631 18.4645 1.67157L21.2929 4.5L18.4645 7.32843C18.2692 7.52369 18.2692 7.84027 18.4645 8.03553C18.6597 8.2308 18.9763 8.2308 19.1716 8.03553L22.3536 4.85355ZM0 4.5V5H22V4.5V4H0V4.5Z"
                    fill="var(--c-text_primary)"
                  />
                </svg>
              </span>
            </nuxt-link>
          </div>
        </div>

        <div
          class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider"
          data-anim="slide-left delay-1"
          data-gap="30"
          data-slider-cols="xl-3 lg-2 md-1 sm-1 base-1"
        >
          <div class="swiper-wrapper">
            <div
              class="swiper-slide"
              v-for="(property, index) in properties"
              :key="`latestproperties-property-${index}`"
            >
              <theme7-properties-property-list-item
                :property="property"
                :is-match-information-hidden="true"
                :pre-market-property-feature="true"
                :pre-market-blur-text="vars.preMarketBlurredImageTextHtml"
                version="v3"
              />
            </div>
          </div>

          <div class="d-flex mt-60 md:mt-30">
            <button class="sliderNav -prev js-prev n-link" @click="slidePrev">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next ml-20 js-next n-link" @click="slideNext">
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>

        <div class="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-2 tw-pt-10 lg:tw-flex-row xl:tw-hidden">
          <nuxt-link
            v-for="button in buttons"
            :key="`latestproperties-button-${button.label}`"
            :to="button.link ? String(button.link) : '#'"
            :external="true"
            :class="`tw-inline-flex tw-w-auto n-${button.type}`"
          >
            {{ button.label }}
            <span
              v-if="company?.company_key === 'keystonepmc' && button.type?.toString().includes('secondary icon')"
              class="btn-arrow"
            >
              <svg width="23" height="9" viewBox="0 0 23 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22.3536 4.85355C22.5488 4.65829 22.5488 4.34171 22.3536 4.14645L19.1716 0.964466C18.9763 0.769204 18.6597 0.769204 18.4645 0.964466C18.2692 1.15973 18.2692 1.47631 18.4645 1.67157L21.2929 4.5L18.4645 7.32843C18.2692 7.52369 18.2692 7.84027 18.4645 8.03553C18.6597 8.2308 18.9763 8.2308 19.1716 8.03553L22.3536 4.85355ZM0 4.5V5H22V4.5V4H0V4.5Z"
                  fill="var(--c-text_primary)"
                />
              </svg>
            </span>
          </nuxt-link>
        </div>
      </div>
    </section>
  </template>

  <template v-else-if="sidebarComponents.length > 0 && variants.v3 !== variant">
    <div
      class="tw-px-4 tw-py-9"
      :style="`background-color: var(--c-${vars.backgroundColor}); border-radius: var(--rounded); border: 1px solid var(--c-border);`"
    >
      <h4 v-html="vars.titleHtml"></h4>

      <div class="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-gap-10">
        <div
          class="relative js-section-slider tw-mt-10"
          data-anim="slide-left delay-1"
          data-gap="30"
          data-slider-cols="xl-1 lg-1 md-1 sm-1 base-1"
        >
          <Swiper
            :key="'property-swiper-' + properties.length"
            :modules="swiperModules"
            :pagination="false"
            :slides-per-view="1"
            :space-between="20"
            effect="fade"
            :clickable="true"
            :watch-overflow="true"
            :loop="true"
            @slideChange="onSlideChange"
            @swiper="onSwiper"
            class="tw-w-full"
          >
            <SwiperSlide v-for="(propertyItem, index) in properties" :key="`latestproperties-property-${index}`">
              <theme7-properties-property-list-item
                :component="component"
                :property="propertyItem"
                :is-match-information-hidden="true"
                :version="componentVersion"
                :pre-market-property-feature="true"
                :pre-market-blur-text="vars.preMarketBlurredImageTextHtml"
                :variables="vars"
              />
            </SwiperSlide>
          </Swiper>

          <div class="d-flex mt-60 md:mt-30 max-lg:tw-items-center max-lg:tw-justify-center">
            <button class="sliderNav -prev n-link" @click="slidePrev">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next ml-20 n-link" @click="slideNext">
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>

  <template v-else-if="variants.v3 === variant && sidebarComponents.length > 0">
    <div
      class="tw-px-4 tw-py-9"
      :style="`background-color: var(--c-${vars.backgroundColor}); border-radius: var(--rounded); border: 1px solid var(--c-border);`"
    >
      <h4 v-html="vars.titleHtml"></h4>

      <div class="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-gap-10">
        <div
          class="relative js-section-slider tw-mt-10"
          data-anim="slide-left delay-1"
          data-gap="30"
          data-slider-cols="xl-1 lg-1 md-1 sm-1 base-1"
        >
          <Swiper
            :key="'property-swiper-' + properties.length"
            :modules="swiperModules"
            :pagination="false"
            :slides-per-view="1"
            :space-between="20"
            effect="fade"
            :clickable="true"
            :watch-overflow="true"
            :loop="true"
            @slideChange="onSlideChange"
            @swiper="onSwiper"
            class="tw-w-full"
          >
            <SwiperSlide v-for="(property, index) in properties" :key="`latestproperties-property-${index}`">
              <theme7-properties-property-list-item
                :property="property"
                :is-match-information-hidden="true"
                version="v2"
                :pre-market-property-feature="true"
                :pre-market-blur-text="vars.preMarketBlurredImageTextHtml"
              />
            </SwiperSlide>
          </Swiper>

          <div class="d-flex mt-60 md:mt-30 max-lg:tw-items-center max-lg:tw-justify-center">
            <button class="sliderNav -prev n-link" @click="slidePrev">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next ml-20 n-link" @click="slideNext">
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
import { PropertiesMixin } from '~/units/properties/mixins/properties.mixin';
import { mapState } from 'pinia';
import { useWebsiteStore } from '~/units/website/store';
import { Pagination as SwiperPagination } from 'swiper/modules';

interface ComponentData {
  swiper: any | null;
  canPaginateBack: boolean;
  selectedProperty: any | null;
  isVisible: boolean;
  canPaginateNext: boolean;
  variants: {
    v1: string;
    v2: string;
    v3: string;
  };
  swiperModules: any[];
}

export default defineNuxtComponent({
  name: 'Theme7LatestProperties',

  mixins: [ComponentMixin, PropertiesMixin],

  async setup(props: any) {
    try {
      await fetchPropertiesManager(props.component, null);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  },

  mounted() {
    try {
      this.onSlideChange();
    } catch (error) {
      console.error('Error in mounted hook:', error);
    }
  },

  data(): ComponentData {
    return {
      swiper: null,
      canPaginateBack: true,
      selectedProperty: null,
      isVisible: false,
      canPaginateNext: true,
      variants: {
        v1: 'v1',
        v2: 'v2',
        v3: 'v3',
      },
      swiperModules: [SwiperPagination],
    };
  },

  computed: {
    ...mapState(useWebsiteStore, ['sidebarComponents', 'company']),
    loopedProperties() {
      try {
        return this.properties ? [...this.properties, ...this.properties] : [];
      } catch (error) {
        console.error('Error in loopedProperties computed:', error);
        return [];
      }
    },

    mixedProperties() {
      try {
        if (!this.properties || this.properties.length === 0) return [];

        const rentProperties = this.properties.filter((property) => property.is_rent);
        const saleProperties = this.properties.filter((property) => !property.is_rent);

        const result = [];
        const maxLength = Math.max(rentProperties.length, saleProperties.length);

        for (let i = 0; i < maxLength; i++) {
          if (i < saleProperties.length) result.push(saleProperties[i]);
          if (i < rentProperties.length) result.push(rentProperties[i]);
        }

        return result;
      } catch (error) {
        console.error('Error in mixedProperties computed:', error);
        return [];
      }
    },

    buttons() {
      return this.groupedVariables.buttons;
    },

    componentVersion() {
      const websiteStore = useWebsiteStore();
      const key = websiteStore.company?.company_key;

      if (key === 'harrisandwood') {
        return 'v2';
      }

      if (key === 'copeandco' || key === 'keystonepmc' || key === 'kineticestateagents') {
        return 'v1';
      }

      return 'v1';
    },
  },

  methods: {
    onSwiper(swiper: any) {
      try {
        if (swiper) {
          this.swiper = swiper;
        }
      } catch (error) {
        console.error('Error in onSwiper:', error);
      }
    },

    onSlideChange() {
      try {
        if (!this.swiper) return;

        const isAtStart = this.swiper.isBeginning;
        const isAtEnd = this.swiper.isEnd;

        this.canPaginateBack = !isAtStart;
        this.canPaginateNext = !isAtEnd;
      } catch (error) {
        console.error('Error in onSlideChange:', error);
      }
    },

    slidePrev() {
      try {
        if (this.swiper) {
          this.swiper.slidePrev();
        }
      } catch (error) {
        console.error('Error in slidePrev:', error);
      }
    },

    slideNext() {
      try {
        if (this.swiper) {
          this.swiper.slideNext();
        }
      } catch (error) {
        console.error('Error in slideNext:', error);
      }
    },
  },
});
</script>

<style scoped>
::v-deep(.v3-left-arrow),
::v-deep(.v3-right-arrow) {
  margin-top: -100px;
}

::v-deep(.property-list-item) {
  min-height: unset !important;
}
</style>
